import * as Sentry from "@sentry/react";
import { useCallback } from "react";

type Return<V> = {
  get: () => V | null;
  set: (content: V) => void;
  remove: () => void;
};

type Operation = "get" | "set" | "remove";

// 容量超過のように同じ操作が失敗し続ける例外で Sentry のイベントが積み上がらないよう、
// ページを読み込んでから同じ組の例外は一度だけ送る
// 容量超過はブラウザ全体が原因で、生徒ごとに変わる storageKey で分けると件数が増えるため、操作だけで組にする
const reportedErrors = new Set<string>();

const reportKeyOf = (
  error: unknown,
  context: { storageKey: string; operation: Operation },
) => {
  const name =
    error instanceof Error || error instanceof DOMException
      ? error.name
      : "UnknownError";
  if (name === "QuotaExceededError") {
    return `${context.operation}:${name}`;
  }
  return `${context.operation}:${context.storageKey}:${name}`;
};

// NOTE: 以下のように localStorage を使えない環境では例外が発生する
// - サンドボックス化された文書: SecurityError: Failed to read the 'localStorage' property from 'Window': The document is sandboxed and lacks the 'allow-same-origin' flag.
// - サイトデータのブロック: SecurityError: Failed to read the 'localStorage' property from 'Window': Access is denied for this document.
// - 容量超過: DOMException: Failed to execute 'setItem' on 'Storage': Setting the value of 'xxx' exceeded the quota.
// ErrorBoundary まで伝わると画面全体がエラーになるため、ここで握りつぶして保存なしとして扱う
// ただし、利用者の環境が原因の SecurityError 以外（容量超過・不正な JSON など）は把握できるように Sentry に送る
const handleStorageError = (
  error: unknown,
  context: { storageKey: string; operation: Operation },
) => {
  if (error instanceof DOMException && error.name === "SecurityError") {
    return;
  }
  const reportKey = reportKeyOf(error, context);
  if (reportedErrors.has(reportKey)) {
    return;
  }
  reportedErrors.add(reportKey);
  Sentry.captureException(error, { extra: context });
};

export function useLocalStorage<V>(storageKey: string): Return<V> {
  const get = useCallback((): V | null => {
    let str: string | null;
    try {
      str = localStorage.getItem(storageKey);
    } catch (error) {
      handleStorageError(error, { storageKey, operation: "get" });
      return null;
    }
    if (!str) {
      return null;
    }
    try {
      return JSON.parse(str) as V;
    } catch (error) {
      handleStorageError(error, { storageKey, operation: "get" });
      // 壊れた値を残すと次回以降も復元できないため、捨てて次回から正常に戻す
      // ここまで来た時点で localStorage は参照できているので、removeItem は try で包まない
      localStorage.removeItem(storageKey);
      return null;
    }
  }, [storageKey]);

  const set = useCallback(
    (content: V) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(content));
      } catch (error) {
        handleStorageError(error, { storageKey, operation: "set" });
      }
    },
    [storageKey],
  );

  const remove = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      handleStorageError(error, { storageKey, operation: "remove" });
    }
  }, [storageKey]);

  return { get, set, remove };
}
